/**
 * Shared "pick a catalog part → confirm → add" flow. The picker reports a
 * pick via `requestAdd`; this renders the confirm dialog and, on confirm,
 * files a part request for management to accept (POST
 * /technical/part-requests, kind=WORK_ORDER). Both the bottom bar and the
 * service header's +Part button use it so the behaviour is identical.
 *
 * **There used to be a second path here.** A technician holding the
 * DIRECT_ADD grant wrote the part straight onto the SERVICE instead of
 * filing a request. That grant is gone: it was the one way billable work
 * reached a job without a manager seeing it, and the endpoint behind it
 * now refuses unconditionally. Filing a request is the only path, which is
 * why the branch and its second set of dialog copy went with it.
 *
 * `serviceId` is not optional. Without it the request lands on the work
 * order with no service to show it under, which is how a filed request
 * left no trace.
 */
import { useState } from 'react'
import { partLabel } from '@/apis/staffApis'
import type { CatalogPartResponse } from '@/apis/types'
import { useI18n } from '@/i18n/TranslationContext'
import { useCreatePartRequest } from '@/hooks/useStaff'
import { ConfirmDialog } from '@/components/ConfirmDialog'

export function usePartAddConfirm(workOrderId: string, serviceId: string) {
  const { t, lang } = useI18n()
  const requestPart = useCreatePartRequest()
  const [pending, setPending] = useState<CatalogPartResponse | null>(null)

  const onConfirm = () => {
    if (!pending) return
    requestPart.mutate({
      kind: 'WORK_ORDER',
      workOrderId,
      serviceId,
      partId: pending.id,
      partLabel: partLabel(pending, lang),
      quantity: 1,
      note: null,
    })
  }

  const confirmNode = pending ? (
    <ConfirmDialog
      // Says it is going to management, not straight onto the job — which
      // is now the only thing that can happen.
      title={t.confirm.requestPartTitle}
      detail={`${partLabel(pending, lang)} · ${pending.sku}`}
      confirmLabel={t.confirm.requestPartCta}
      tone="primary"
      onConfirm={onConfirm}
      onClose={() => setPending(null)}
    />
  ) : null

  return { requestAdd: setPending, confirmNode }
}
